import { useState, useMemo } from 'react';
import { useStore } from '../store/useStore';
import {
  Gift, QrCode, UserCheck, History, Sparkles, Plus, Trash2,
  Settings, Search, Award, TrendingUp, RefreshCw, Clock, Link, X, CheckCircle
} from 'lucide-react';
import { Scanner } from '@yudiel/react-qr-scanner';

export function TeamOffersView() {
  const {
    team,
    offerProducts,
    customQuotas,
    consumptions,
    checkins,
    currentOrgId,
    addOfferProduct,
    deleteOfferProduct,
    updateTeamDistributor,
    setCustomQuota,
    deleteTeamConsumption,
    deleteTeamCheckin,
    resetAllQuotas,
    scheduleQuotaReset,
    organizations,
    currentUser,
    sharedTeamOrganizations,
    categories,
    viewAsManagerCategoryId,
    updateTeam
  } = useStore();

  const [activeSubTab, setActiveSubTab] = useState<'feed' | 'rights' | 'products' | 'quotas'>('feed');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Product Form State
  const [newProdName, setNewProdName] = useState('');
  const [newProdIcon, setNewProdIcon] = useState('Beer');
  const [newProdQuota, setNewProdQuota] = useState<number | ''>(5);
  const [newProdPoints, setNewProdPoints] = useState<number | ''>(1);
  // Category attribution mode: 'all' = toutes les catégories, 'specific' = sélection par catégorie
  const [categoryMode, setCategoryMode] = useState<'all' | 'specific'>('all');
  // Per-category quotas when mode is 'specific': Map<categoryId, quota>
  const [categoryQuotas, setCategoryQuotas] = useState<Record<string, number>>({});

  // Wallet QR linking (admin scans QR then assigns to team)
  const [walletScanOpen, setWalletScanOpen] = useState(false);
  const [scannedWalletValue, setScannedWalletValue] = useState<string | null>(null);
  const [walletTeamSearch, setWalletTeamSearch] = useState('');

  const handleWalletScan = (text: string) => {
    if (!text || !text.trim()) return;
    setScannedWalletValue(text.trim());
    setWalletScanOpen(false);
    setWalletTeamSearch('');
  };

  const handleAssignWallet = (teamId: string) => {
    const s = team.find(st => st.id === teamId);
    if (!s || !scannedWalletValue) return;
    if (s.linkedWalletQr && s.linkedWalletQr !== scannedWalletValue) {
      if (!window.confirm(`${s.name} a déjà un QR lié. Voulez-vous le remplacer ?`)) return;
    }
    updateTeam(teamId, { linkedWalletQr: scannedWalletValue });
    setScannedWalletValue(null);
  };

  // Quota Adjust State
  const [selectedTeamForQuota, setSelectedTeamForQuota] = useState<string | null>(null);
  
  // Reset Date state
  const currentOrg = organizations.find(o => o.id === currentOrgId);
  const orgCurrency = currentOrg?.settings?.currency || 'TND';
  const [scheduledReset, setScheduledReset] = useState(
    currentOrg?.settings && (currentOrg.settings as any).scheduled_quota_reset
      ? new Date((currentOrg.settings as any).scheduled_quota_reset).toISOString().slice(0, 16)
      : ''
  );

  // Filtering — includes all categories (artists too), unified in Team Offers
  const filteredTeam = useMemo(() => {
    // 1. Base visibility: team in current org (or shared), no artist exclusion, exclude sub-members
    const visibleTeam = team.filter(s => {
      return !s.parentArtistId && (s.orgId === currentOrgId ||
        sharedTeamOrganizations.some(link => link.teamId === s.id && link.orgId === currentOrgId));
    });

    // 2. Manager-specific filtering
    let allowedTeam = visibleTeam;
    const effectiveCategoryId = (currentUser.role === 'manager' && currentUser.categoryId) || viewAsManagerCategoryId;
    if (effectiveCategoryId) {
      allowedTeam = visibleTeam.filter(s => s.categoryId === effectiveCategoryId);
    }

    // 3. Search term filtering
    return allowedTeam.filter(s =>
      s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (s.email || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [team, searchTerm, currentOrgId, sharedTeamOrganizations, currentUser, categories]);

  // Wallet team picker — filter by search
  const walletFilteredTeam = useMemo(() => {
    const allOrgTeam = team.filter(s =>
      (s.orgId === currentOrgId ||
        sharedTeamOrganizations.some(link => link.teamId === s.id && link.orgId === currentOrgId))
    );
    if (!walletTeamSearch) return allOrgTeam;
    const q = walletTeamSearch.toLowerCase();
    return allOrgTeam.filter(s => s.name.toLowerCase().includes(q) || (s.email || '').toLowerCase().includes(q));
  }, [team, currentOrgId, sharedTeamOrganizations, walletTeamSearch]);

  // Statistics
  const stats = useMemo(() => {
    const totalConsPoints = consumptions.reduce((sum, c) => sum + (c.pointsValue * c.quantity), 0);
    const totalConsCount = consumptions.reduce((sum, c) => sum + c.quantity, 0);
    const totalCheckinsCount = checkins.length;
    
    // Top consumed product
    const prodCounts: Record<string, number> = {};
    consumptions.forEach(c => {
      prodCounts[c.productName] = (prodCounts[c.productName] || 0) + c.quantity;
    });
    let topProduct = 'Aucun';
    let maxCount = 0;
    Object.entries(prodCounts).forEach(([name, count]) => {
      if (count > maxCount) {
        maxCount = count;
        topProduct = name;
      }
    });

    return {
      totalConsPoints,
      totalConsCount,
      totalCheckinsCount,
      topProduct: maxCount > 0 ? `${topProduct} (${maxCount}x)` : 'Aucun'
    };
  }, [consumptions, checkins]);

  // Icon options
  const iconOptions = ['Beer', 'Coffee', 'Utensils', 'Pizza', 'GlassWater', 'Gift', 'Ticket', 'Sparkles'];

  // All categories for product attribution (artists included, unified)
  const allCategoriesForOffers = useMemo(() =>
    categories,
  [categories]);

  const handleAddProduct = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProdName.trim()) return;
    const catQuotaEntries = categoryMode === 'specific'
      ? Object.entries(categoryQuotas).filter(([_, q]) => q > 0).map(([catId, q]) => ({ categoryId: catId, quota: q }))
      : undefined;
    addOfferProduct({
      name: newProdName.trim(),
      icon: newProdIcon,
      defaultQuota: categoryMode === 'specific' ? 0 : (newProdQuota === '' ? 0 : newProdQuota),
      pointsValue: newProdPoints === '' ? 1 : newProdPoints
    }, catQuotaEntries);
    setNewProdName('');
    setNewProdQuota(5);
    setNewProdPoints(1);
    setCategoryMode('all');
    setCategoryQuotas({});
  };

  const handleSaveScheduledReset = () => {
    if (!currentOrgId) return;
    scheduleQuotaReset(currentOrgId, scheduledReset ? new Date(scheduledReset).toISOString() : null);
    alert('Date de réinitialisation programmée mise à jour avec succès !');
  };

  const handleInstantReset = () => {
    if (!currentOrgId) return;
    if (confirm('Êtes-vous sûr de vouloir réinitialiser instantanément tous les quotas de consommation pour l\'ensemble du team ? Cette action est irréversible.')) {
      resetAllQuotas(currentOrgId);
      alert('Tous les quotas ont été réinitialisés avec succès !');
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-[#0A0B0E] text-slate-100 p-3 sm:p-4 md:p-6 w-full max-w-full custom-scrollbar" style={{WebkitOverflowScrolling: 'touch'}}>
      <div className="flex flex-col min-h-full">
      {/* Premium Header */}
      <div className="flex-none flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-[#1E222B]">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="p-2.5 bg-indigo-500/10 rounded-xl border border-indigo-500/20 text-indigo-400">
              <Gift className="w-6 h-6 animate-pulse" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-white break-words">Team Offers & Avantages Team</h1>
              <p className="text-xs sm:text-sm text-slate-400 mt-1">Gérez les boissons gratuites, repas, check-ins et points de présence de vos équipes.</p>
            </div>
          </div>
        </div>
        
        {/* Reset Actions */}
        <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 bg-[#111319] p-3 rounded-xl border border-[#222630] min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-xs text-slate-400 min-w-0">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 flex-none text-indigo-400" />
              <span className="flex-none">Réinit Auto :</span>
            </div>
            <div className="flex flex-row items-center gap-2 w-full sm:w-auto">
              <input 
                type="datetime-local" 
                value={scheduledReset}
                onChange={(e) => setScheduledReset(e.target.value)}
                className="flex-1 sm:flex-none w-full sm:w-auto min-w-0 bg-[#0A0B0E] text-slate-200 border border-[#2D3139] px-2 py-1.5 rounded text-xs focus:ring-1 focus:ring-indigo-500 outline-none"
              />
              <button 
                onClick={handleSaveScheduledReset}
                className="flex-none bg-indigo-600 hover:bg-indigo-700 text-white px-2.5 py-1.5 rounded text-xs font-semibold transition-all"
              >
                Planifier
              </button>
            </div>
          </div>
          <div className="w-full h-px sm:w-px sm:h-6 bg-[#222630]" />
          <button
            onClick={handleInstantReset}
            className="flex-none flex items-center justify-center sm:justify-start gap-1.5 bg-rose-500/15 hover:bg-rose-500/25 text-rose-400 border border-rose-500/20 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all w-full sm:w-auto"
          >
            <RefreshCw className="w-3.5 h-3.5 flex-none" />
            <span className="truncate">Remise à zéro immédiate</span>
          </button>
        </div>
      </div>

      {/* Grid Stats */}
      <div className="flex-none grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 py-6">
        <div className="bg-[#111319] p-4 rounded-xl border border-[#1E222B] flex items-center justify-between shadow-lg">
          <div>
            <span className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Total Consommé</span>
            <h3 className="text-2xl font-bold text-white mt-1">{stats.totalConsCount} produits</h3>
          </div>
          <div className="p-3 bg-indigo-500/10 rounded-lg text-indigo-400">
            <Gift className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-[#111319] p-4 rounded-xl border border-[#1E222B] flex items-center justify-between shadow-lg">
          <div>
            <span className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Valeur Totale</span>
            <h3 className="text-2xl font-bold text-emerald-400 mt-1">{stats.totalConsPoints} {orgCurrency}</h3>
          </div>
          <div className="p-3 bg-emerald-500/10 rounded-lg text-emerald-400">
            <TrendingUp className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-[#111319] p-4 rounded-xl border border-[#1E222B] flex items-center justify-between shadow-lg">
          <div>
            <span className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Check-ins Validés</span>
            <h3 className="text-2xl font-bold text-cyan-400 mt-1">{stats.totalCheckinsCount} pointages</h3>
          </div>
          <div className="p-3 bg-cyan-500/10 rounded-lg text-cyan-400">
            <UserCheck className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-[#111319] p-4 rounded-xl border border-[#1E222B] flex items-center justify-between shadow-lg">
          <div>
            <span className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Top Produit</span>
            <h3 className="text-lg font-bold text-amber-400 mt-2 truncate max-w-[170px]">{stats.topProduct}</h3>
          </div>
          <div className="p-3 bg-amber-500/10 rounded-lg text-amber-400">
            <Award className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Sub-Tabs Nav */}
      <div className="flex-none flex items-center gap-1.5 bg-[#111319] p-1.5 rounded-xl border border-[#1E222B] mb-6 overflow-x-auto custom-scrollbar w-full max-w-full">
        <button
          onClick={() => setActiveSubTab('feed')}
          className={`flex-none flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-semibold transition-all ${activeSubTab === 'feed' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400 hover:text-slate-200 hover:bg-[#1C1F26]'}`}
        >
          <History className="w-4 h-4" />
          Flux Temps Réel
        </button>
        <button
          onClick={() => setActiveSubTab('rights')}
          className={`flex-none flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-semibold transition-all ${activeSubTab === 'rights' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400 hover:text-slate-200 hover:bg-[#1C1F26]'}`}
        >
          <QrCode className="w-4 h-4" />
          Droits Distributeurs
        </button>
        <button
          onClick={() => setActiveSubTab('products')}
          className={`flex-none flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-semibold transition-all ${activeSubTab === 'products' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400 hover:text-slate-200 hover:bg-[#1C1F26]'}`}
        >
          <Settings className="w-4 h-4" />
          Catalogue Produits
        </button>
        <button
          onClick={() => setActiveSubTab('quotas')}
          className={`flex-none flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-semibold transition-all ${activeSubTab === 'quotas' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400 hover:text-slate-200 hover:bg-[#1C1F26]'}`}
        >
          <Sparkles className="w-4 h-4" />
          Quotas Spécifiques
        </button>
        <button
          onClick={() => setWalletScanOpen(true)}
          className="ml-auto flex-none flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-semibold transition-all bg-purple-600/20 hover:bg-purple-600/30 text-purple-400 border border-purple-500/30"
        >
          <Link className="w-4 h-4" />
          Link Wallet
        </button>
      </div>

      {/* Main Content Area — no inner scroll wrapper, page scrolls naturally */}
      <div className="flex-1">
        {/* SUBTAB 1: LIVE FEED ACTIVITY STREAM */}
        {activeSubTab === 'feed' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-[#111319] rounded-xl border border-[#1E222B] p-5 flex flex-col h-[400px]">
              <div className="flex items-center justify-between pb-4 border-b border-[#222630] mb-4">
                <h3 className="text-sm font-bold text-white flex items-center gap-2">
                  <span className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-ping" />
                  Flux en temps réel des Scans & Pointages
                </h3>
                <span className="text-xs text-slate-400">Total : {consumptions.length + checkins.length} actions</span>
              </div>
              
              <div className="flex-1 overflow-y-auto custom-scrollbar space-y-3">
                {consumptions.length === 0 && checkins.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-slate-500">
                    <History className="w-10 h-10 mb-2 opacity-30" />
                    <p className="text-xs">Aucune activité enregistrée pour le moment.</p>
                  </div>
                ) : (
                  [
                    ...consumptions.map(c => ({ ...c, type: 'consumption' as const })),
                    ...checkins.map(ch => ({ ...ch, type: 'checkin' as const, productName: 'Check-in Présence', quantity: 1 }))
                  ]
                  .sort((a,b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                  .map((act) => {
                    const teamMember = team.find(s => s.id === act.teamId);
                    const distributor = team.find(s => s.id === act.scannedBy);
                    return (
                      <div key={act.id} className="p-3 bg-[#181B22] rounded-lg border border-[#222630] flex items-center justify-between gap-4 transition-all hover:bg-[#1F232D]">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${act.type === 'consumption' ? 'bg-indigo-500/10 text-indigo-400' : 'bg-cyan-500/10 text-cyan-400'}`}>
                            {act.type === 'consumption' ? <Gift className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                          </div>
                          <div>
                            <div className="text-xs font-semibold">
                              <span className="text-indigo-400 font-bold">{teamMember?.name || 'Team inconnu'}</span>
                              <span className="text-slate-400"> a reçu </span>
                              <span className="text-white font-bold">{act.quantity}x {act.productName}</span>
                            </div>
                            <div className="text-[10px] text-slate-500 mt-0.5">
                              Distribué par : <span className="text-slate-300 font-medium">{distributor?.name || 'Administrateur'}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <div className="text-[10px] text-slate-500 font-medium">
                              {new Date(act.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                            </div>
                            {act.type === 'consumption' && (
                              <span className="inline-block text-[9px] bg-emerald-500/10 text-emerald-400 font-bold px-1.5 py-0.5 rounded mt-1">
                                +{act.pointsValue * act.quantity} pts
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => {
                              if (window.confirm(`Supprimer cette action (${act.productName}) ?`)) {
                                if (act.type === 'consumption') {
                                  deleteTeamConsumption(act.id, currentUser.email || 'admin');
                                } else {
                                  deleteTeamCheckin(act.id, currentUser.email || 'admin');
                                }
                              }
                            }}
                            className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-all"
                            title="Annuler/Supprimer cette action"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Live Feed Sidebar statistics */}
            <div className="space-y-4">
              <div className="bg-[#111319] p-5 rounded-xl border border-[#1E222B]">
                <h3 className="text-sm font-bold text-white mb-3">Statistiques par Produit</h3>
                <div className="space-y-3">
                  {offerProducts.length === 0 ? (
                    <p className="text-xs text-slate-500">Aucun produit configuré.</p>
                  ) : (
                    offerProducts.map(prod => {
                      const count = consumptions.filter(c => c.productName === prod.name).reduce((sum, c) => sum + c.quantity, 0);
                      const percent = stats.totalConsCount > 0 ? (count / stats.totalConsCount) * 100 : 0;
                      return (
                        <div key={prod.id}>
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="font-semibold text-slate-300">{prod.name}</span>
                            <span className="text-slate-400">{count} cons. ({Math.round(percent)}%)</span>
                          </div>
                          <div className="w-full h-1.5 bg-[#1C1F26] rounded-full overflow-hidden">
                            <div className="h-full bg-indigo-500" style={{ width: `${percent}%` }} />
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SUBTAB 2: DISTRIBUTOR RIGHTS AND ACCESS CONTROL */}
        {activeSubTab === 'rights' && (
          <div className="bg-[#111319] rounded-xl border border-[#1E222B] p-5">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-[#222630] mb-6">
              <div>
                <h3 className="text-sm font-bold text-white">Désignation des Distributeurs & Autorisations</h3>
                <p className="text-xs text-slate-400 mt-1">Cochez les droits pour chaque membre. Survolez les entêtes de colonne pour voir les descriptions. Les cases grisées nécessitent une autre permission.</p>
              </div>
              <div className="relative w-full md:w-72">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                <input
                  type="text"
                  placeholder="Rechercher un membre team..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-lg pl-9 pr-4 py-2 text-xs focus:ring-1 focus:ring-indigo-500 outline-none text-slate-200"
                />
              </div>
            </div>

            <div className="w-full overflow-x-auto custom-scrollbar">
              <table className="w-full text-left text-xs text-slate-300">
                <thead>
                  <tr className="border-b border-[#222630] text-slate-400">
                    <th className="pb-2 pt-1 font-semibold">Nom Team</th>
                    <th className="pb-2 pt-1 font-semibold">Rôle Principal</th>
                    <th className="pb-2 pt-1 font-semibold text-center">Accès<br/>Distributeur</th>
                    <th className="pb-2 pt-1 font-semibold text-center">Scanner<br/>QR Code</th>
                    <th className="pb-2 pt-1 font-semibold text-center">Inscrire<br/>Directement</th>
                    <th className="pb-2 pt-1 font-semibold text-center">Check-in<br/>Présence</th>
                    <th className="pb-2 pt-1 font-semibold text-center">Voir<br/>Liste</th>
                    <th className="pb-2 pt-1 font-semibold text-center">Link<br/>Wallet</th>
                  </tr>
                  <tr className="border-b border-[#222630] text-[9px] text-slate-600 leading-tight">
                    <th className="pb-3"></th>
                    <th className="pb-3"></th>
                    <th className="pb-3 text-center font-normal px-1">Donne accès à la caisse<br/>et au panier de<br/>consommations</th>
                    <th className="pb-3 text-center font-normal px-1">Scanner un badge QR<br/>pour identifier<br/>un membre</th>
                    <th className="pb-3 text-center font-normal px-1">Rechercher un membre<br/>manuellement sans scan<br/>(nécessite Distributeur)</th>
                    <th className="pb-3 text-center font-normal px-1">Valider la présence<br/>d'un membre au point<br/>de distribution</th>
                    <th className="pb-3 text-center font-normal px-1">Voir la liste des membres<br/>et leurs quotas<br/>(consultation seule)</th>
                    <th className="pb-3 text-center font-normal px-1">Associer un QR code<br/>wallet à un membre<br/>(nécessite Scan ou Liste)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#222630]">
                  {filteredTeam.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-6 text-center text-slate-500">Aucun membre team trouvé.</td>
                    </tr>
                  ) : (
                    filteredTeam.map(member => {
                      const canLinkWallet = (member.distAllowScan || member.distAllowView);
                      const canDirect = member.isDistributor;
                      return (
                      <tr key={member.id} className="hover:bg-[#161922] transition-colors">
                        <td className="py-3.5 font-bold text-white">{member.name}</td>
                        <td className="py-3.5 text-slate-400">
                          <span className="bg-[#1C1F26] px-2 py-0.5 rounded text-[10px] font-medium border border-[#2D3139]">
                            {member.roles?.join(', ') || member.role || 'Général'}
                          </span>
                        </td>
                        <td className="py-3.5 text-center">
                          <input
                            type="checkbox"
                            checked={member.isDistributor || false}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              const updates: any = { isDistributor: checked };
                              // Si on décoche Distributeur, décocher aussi Inscrire Directement
                              if (!checked) updates.distAllowDirect = false;
                              updateTeamDistributor(member.id, updates);
                            }}
                            className="w-4 h-4 text-indigo-600 border-slate-600 bg-[#0A0B0E] rounded focus:ring-indigo-500 focus:ring-offset-[#111319]"
                          />
                        </td>
                        <td className="py-3.5 text-center">
                          <input
                            type="checkbox"
                            checked={member.distAllowScan || false}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              const updates: any = { distAllowScan: checked };
                              // Si on décoche Scanner ET que Voir Liste est aussi décoché, décocher Link Wallet
                              if (!checked && !member.distAllowView) updates.distAllowLinkWallet = false;
                              updateTeamDistributor(member.id, updates);
                            }}
                            className="w-4 h-4 text-indigo-600 border-slate-600 bg-[#0A0B0E] rounded focus:ring-indigo-500 focus:ring-offset-[#111319]"
                          />
                        </td>
                        <td className="py-3.5 text-center">
                          <input
                            type="checkbox"
                            checked={member.distAllowDirect || false}
                            disabled={!canDirect}
                            onChange={(e) => updateTeamDistributor(member.id, { distAllowDirect: e.target.checked })}
                            className={`w-4 h-4 border-slate-600 bg-[#0A0B0E] rounded focus:ring-indigo-500 focus:ring-offset-[#111319] ${canDirect ? 'text-indigo-600' : 'text-slate-700 opacity-40 cursor-not-allowed'}`}
                          />
                        </td>
                        <td className="py-3.5 text-center">
                          <input
                            type="checkbox"
                            checked={member.distAllowCheckin || false}
                            onChange={(e) => updateTeamDistributor(member.id, { distAllowCheckin: e.target.checked })}
                            className="w-4 h-4 text-indigo-600 border-slate-600 bg-[#0A0B0E] rounded focus:ring-indigo-500 focus:ring-offset-[#111319]"
                          />
                        </td>
                        <td className="py-3.5 text-center">
                          <input
                            type="checkbox"
                            checked={member.distAllowView || false}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              const updates: any = { distAllowView: checked };
                              // Si on décoche Voir Liste ET que Scanner est aussi décoché, décocher Link Wallet
                              if (!checked && !member.distAllowScan) updates.distAllowLinkWallet = false;
                              updateTeamDistributor(member.id, updates);
                            }}
                            className="w-4 h-4 text-indigo-600 border-slate-600 bg-[#0A0B0E] rounded focus:ring-indigo-500 focus:ring-offset-[#111319]"
                          />
                        </td>
                        <td className="py-3.5 text-center">
                          <input
                            type="checkbox"
                            checked={member.distAllowLinkWallet || false}
                            disabled={!canLinkWallet}
                            onChange={(e) => updateTeamDistributor(member.id, { distAllowLinkWallet: e.target.checked })}
                            className={`w-4 h-4 border-slate-600 bg-[#0A0B0E] rounded focus:ring-indigo-500 focus:ring-offset-[#111319] ${canLinkWallet ? 'text-indigo-600' : 'text-slate-700 opacity-40 cursor-not-allowed'}`}
                          />
                        </td>
                      </tr>
                    )})
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* SUBTAB 3: TEAM OFFERS PRODUCTS CATALOGUE */}
        {activeSubTab === 'products' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Products Form */}
            <div className="bg-[#111319] p-5 rounded-xl border border-[#1E222B]">
              <h3 className="text-sm font-bold text-white pb-3 border-b border-[#222630] mb-4">Créer un nouveau produit</h3>
              <form onSubmit={handleAddProduct} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-1.5">Nom du produit</label>
                  <input
                    type="text"
                    required
                    placeholder="Bière, Repas, Bouteille..."
                    value={newProdName}
                    onChange={(e) => setNewProdName(e.target.value)}
                    className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-lg px-3 py-2 text-xs focus:ring-1 focus:ring-indigo-500 outline-none text-slate-200"
                  />
                </div>

                {categoryMode === 'all' ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-400 uppercase mb-1.5">Quota par défaut</label>
                      <input
                        type="number"
                        required
                        min={0}
                        value={newProdQuota}
                        onChange={(e) => setNewProdQuota(Number(e.target.value))}
                        className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-lg px-3 py-2 text-xs focus:ring-1 focus:ring-indigo-500 outline-none text-slate-200"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-400 uppercase mb-1.5">Valeur ({orgCurrency})</label>
                      <input
                        type="number"
                        required
                        min={0}
                        value={newProdPoints}
                        onChange={(e) => setNewProdPoints(Number(e.target.value))}
                        className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-lg px-3 py-2 text-xs focus:ring-1 focus:ring-indigo-500 outline-none text-slate-200"
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-semibold text-slate-400 uppercase mb-1.5">Valeur ({orgCurrency})</label>
                    <input
                      type="number"
                      required
                      min={0}
                      value={newProdPoints}
                      onChange={(e) => setNewProdPoints(Number(e.target.value))}
                      className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-lg px-3 py-2 text-xs focus:ring-1 focus:ring-indigo-500 outline-none text-slate-200"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-1.5">Icône visuelle</label>
                  <div className="grid grid-cols-4 gap-2">
                    {iconOptions.map(ico => (
                      <button
                        key={ico}
                        type="button"
                        onClick={() => setNewProdIcon(ico)}
                        className={`p-2 rounded-lg border text-xs flex items-center justify-center ${newProdIcon === ico ? 'bg-indigo-600 border-indigo-500 text-white font-bold' : 'bg-[#0A0B0E] border-[#2D3139] text-slate-400 hover:text-white'}`}
                      >
                        {ico}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Category Attribution */}
                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Attribution par catégorie</label>
                  <div className="flex gap-3 mb-3">
                    <button
                      type="button"
                      onClick={() => setCategoryMode('all')}
                      className={`flex-1 py-2 rounded-lg border text-xs font-bold transition-all ${categoryMode === 'all' ? 'bg-indigo-600 border-indigo-500 text-white' : 'bg-[#0A0B0E] border-[#2D3139] text-slate-400 hover:text-white'}`}
                    >
                      Toutes les catégories
                    </button>
                    <button
                      type="button"
                      onClick={() => setCategoryMode('specific')}
                      className={`flex-1 py-2 rounded-lg border text-xs font-bold transition-all ${categoryMode === 'specific' ? 'bg-indigo-600 border-indigo-500 text-white' : 'bg-[#0A0B0E] border-[#2D3139] text-slate-400 hover:text-white'}`}
                    >
                      Par catégorie
                    </button>
                  </div>

                  {categoryMode === 'specific' && (
                    <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar p-2 bg-[#0A0B0E] rounded-lg border border-[#2D3139]">
                      {allCategoriesForOffers.length === 0 ? (
                        <p className="text-[10px] text-slate-500 text-center py-2">Aucune catégorie disponible</p>
                      ) : (
                        allCategoriesForOffers.map(cat => (
                          <div key={cat.id} className="flex items-center gap-2">
                            <span className="text-[10px] text-slate-300 flex-1 truncate">{cat.name}</span>
                            <input
                              type="number"
                              min={0}
                              placeholder="Quota"
                              value={categoryQuotas[cat.id] ?? ''}
                              onChange={(e) => {
                                const val = e.target.value === '' ? 0 : Math.max(0, Number(e.target.value));
                                setCategoryQuotas(prev => ({ ...prev, [cat.id]: val }));
                              }}
                              className="w-16 bg-[#14161B] border border-[#2D3139] rounded px-2 py-1 text-[10px] text-slate-200 focus:ring-1 focus:ring-indigo-500 outline-none text-center"
                            />
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 rounded-lg text-xs transition-all shadow-md mt-2"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter au catalogue
                </button>
              </form>
            </div>

            {/* Products List */}
            <div className="lg:col-span-2 bg-[#111319] p-5 rounded-xl border border-[#1E222B]">
              <h3 className="text-sm font-bold text-white pb-3 border-b border-[#222630] mb-4">Produits Opérationnels du Catalogue</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {offerProducts.length === 0 ? (
                  <div className="col-span-2 py-8 text-center text-slate-500">
                    Aucun produit dans le catalogue. Utilisez le formulaire à gauche pour en ajouter.
                  </div>
                ) : (
                  offerProducts.map(prod => (
                    <div key={prod.id} className="p-4 bg-[#181B22] rounded-xl border border-[#222630] flex items-center justify-between gap-4 transition-all hover:bg-[#1F232D]">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 rounded-lg bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 flex items-center justify-center text-sm font-bold shrink-0">
                          {prod.icon.charAt(0)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="text-xs font-bold text-white truncate">{prod.name}</h4>
                          <div className="text-[10px] text-slate-400 mt-1 flex flex-col gap-0.5">
                            <span>Quota par défaut : <strong className="text-indigo-300 font-bold">{prod.defaultQuota}</strong></span>
                            <span>Valeur : <strong className="text-emerald-400 font-bold">+{prod.pointsValue} {orgCurrency}</strong></span>
                            {/* Show category attribution */}
                            {(() => {
                              const catQuotas = prod.categoryQuotas || [];
                              if (catQuotas.length > 0) {
                                return (
                                  <span className="text-purple-400">
                                    Catégories : {catQuotas.map(q => {
                                      const cat = categories.find(c => c.id === q.categoryId);
                                      return cat ? `${cat.name} (×${q.quota})` : '';
                                    }).filter(Boolean).join(', ')}
                                  </span>
                                );
                              }
                              return <span className="text-slate-500">Toutes catégories</span>;
                            })()}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          if (confirm(`Êtes-vous sûr de vouloir supprimer ${prod.name} du catalogue ?`)) {
                            deleteOfferProduct(prod.id);
                          }
                        }}
                        className="p-2 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/20 text-rose-400 rounded-lg transition-all"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* SUBTAB 4: CUSTOM QUOTAS PER TEAM MEMBER */}
        {activeSubTab === 'quotas' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Team list */}
            <div className="bg-[#111319] p-5 rounded-xl border border-[#1E222B] flex flex-col h-[400px]">
              <h3 className="text-sm font-bold text-white pb-3 border-b border-[#222630] mb-4">Sélectionner un membre</h3>
              <div className="relative mb-3 flex-none">
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-lg pl-8 pr-3 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500 outline-none text-slate-200"
                />
              </div>
              <div className="flex-1 overflow-y-auto custom-scrollbar space-y-2">
                {filteredTeam.map(member => (
                  <button
                    key={member.id}
                    onClick={() => setSelectedTeamForQuota(member.id)}
                    className={`w-full text-left p-3 rounded-lg border text-xs font-semibold flex items-center justify-between transition-all ${selectedTeamForQuota === member.id ? 'bg-indigo-600/10 border-indigo-500 text-white shadow-md' : 'bg-[#181B22] border-[#222630] text-slate-400 hover:text-white'}`}
                  >
                    <span>{member.name}</span>
                    <span className="text-[10px] text-slate-500">{member.roles?.join(', ') || 'Général'}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Quota adjustments */}
            <div className="lg:col-span-2 bg-[#111319] p-5 rounded-xl border border-[#1E222B]">
              <h3 className="text-sm font-bold text-white pb-3 border-b border-[#222630] mb-4">
                Ajustement des Quotas Personnalisés
              </h3>
              {!selectedTeamForQuota ? (
                <div className="py-12 text-center text-slate-500 flex flex-col items-center justify-center">
                  <Sparkles className="w-10 h-10 mb-2 opacity-30 text-indigo-400" />
                  <p className="text-xs">Sélectionnez un membre dans le panneau de gauche pour ajuster ses quotas individuels.</p>
                </div>
              ) : (
                <div>
                  <div className="mb-4 bg-indigo-500/5 p-3 rounded-lg border border-indigo-500/10">
                    <span className="text-xs text-slate-400 uppercase font-semibold">Team Sélectionné :</span>
                    <h4 className="text-sm font-bold text-white mt-0.5">
                      {team.find(s => s.id === selectedTeamForQuota)?.name}
                    </h4>
                  </div>

                  {offerProducts.length === 0 ? (
                    <p className="text-xs text-slate-500 py-6 text-center">Aucun produit configuré dans le catalogue pour le moment.</p>
                  ) : (
                    <div className="space-y-4">
                      {offerProducts.map(prod => {
                        const teamMember = team.find(s => s.id === selectedTeamForQuota);
                        const custom = customQuotas.find(q => q.teamId === selectedTeamForQuota && q.productName === prod.name);
                        // Resolve effective default from product's embedded categoryQuotas
                        const catQuotasForProd = prod.categoryQuotas || [];
                        let effectiveDefault = prod.defaultQuota;
                        if (catQuotasForProd.length > 0) {
                          if (teamMember?.categoryId) {
                            const match = catQuotasForProd.find(q => q.categoryId === teamMember.categoryId);
                            effectiveDefault = match ? match.quota : 0;
                          } else {
                            effectiveDefault = 0;
                          }
                        }
                        const currentVal = custom ? custom.customQuota : effectiveDefault;
                        return (
                          <div key={prod.id} className="p-3 bg-[#181B22] rounded-lg border border-[#222630] flex items-center justify-between gap-4">
                            <div>
                              <h5 className="text-xs font-bold text-white">{prod.name}</h5>
                              <p className="text-[10px] text-slate-500 mt-0.5">Quota standard du catalogue : {prod.defaultQuota}</p>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-slate-400">Quota Spécifique :</span>
                              <input
                                type="number"
                                min={0}
                                value={currentVal === 0 ? '' : currentVal}
                                placeholder="0"
                                onChange={(e) => setCustomQuota(selectedTeamForQuota, prod.name, e.target.value === '' ? 0 : (parseInt(e.target.value) || 0))}
                                className="w-20 bg-[#0A0B0E] border border-[#2D3139] rounded px-2.5 py-1 text-center text-xs text-emerald-400 font-bold focus:ring-1 focus:ring-indigo-500 outline-none"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

      </div>  {/* close flex-1 content area */}

      {/* Wallet QR Scanner Modal */}
      {walletScanOpen && (
        <div className="fixed inset-0 bg-[#0A0B0E]/95 backdrop-blur-sm z-[60] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-[#2D3139]">
            <span className="font-bold text-white text-lg">Scanner un QR Code</span>
            <button onClick={() => setWalletScanOpen(false)} className="p-2 bg-[#1A1D23] rounded-xl text-slate-400 hover:text-white border border-[#2D3139]">✕</button>
          </div>
          <div className="flex-1 relative">
            <Scanner onScan={(result) => result && handleWalletScan(result[0].rawValue)} onError={() => {}} />
          </div>
          <div className="p-4 border-t border-[#2D3139]">
            <p className="text-[9px] text-slate-500 text-center">Scannez le bracelet ou carte du team pour le lier à son compte</p>
          </div>
        </div>
      )}

      {/* Wallet Team Picker Modal (after scan) */}
      {scannedWalletValue && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[65] flex items-center justify-center p-4" onClick={() => setScannedWalletValue(null)}>
          <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-[#2D3139] flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-white">Assigner le QR Code</h3>
                <p className="text-[10px] text-purple-400 font-mono mt-1 truncate max-w-[250px]">{scannedWalletValue}</p>
              </div>
              <button onClick={() => setScannedWalletValue(null)} className="p-2 hover:bg-[#1A1D23] rounded-lg text-slate-400">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-3 border-b border-[#2D3139]">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                <input
                  type="text" placeholder="Rechercher un team..."
                  value={walletTeamSearch} onChange={e => setWalletTeamSearch(e.target.value)}
                  autoFocus
                  className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-lg pl-8 pr-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                />
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto custom-scrollbar p-2 space-y-1">
              {walletFilteredTeam.length === 0 ? (
                <p className="text-[10px] text-slate-500 text-center py-4">Aucun team trouvé</p>
              ) : (
                walletFilteredTeam.map(s => (
                  <button
                    key={s.id}
                    onClick={() => handleAssignWallet(s.id)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#1A1D23] transition-colors text-left"
                  >
                    <div className="w-7 h-7 rounded-full bg-purple-500/20 flex items-center justify-center text-[9px] font-bold text-purple-400 shrink-0">
                      {s.name[0]}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] text-slate-200 truncate">{s.name}</div>
                      <div className="text-[9px] text-slate-500 truncate">
                        {s.linkedWalletQr ? '🔗 QR déjà lié' : 'Aucun QR lié'}
                      </div>
                    </div>
                    <CheckCircle className="w-4 h-4 text-slate-600" />
                  </button>
                ))
              )}
            </div>
            <div className="p-3 border-t border-[#2D3139] bg-[#0A0B0E] rounded-b-2xl">
              <button onClick={() => setScannedWalletValue(null)} className="w-full py-2.5 bg-[#1A1D23] hover:bg-[#2D3139] text-slate-300 rounded-lg text-xs font-bold">
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      </div>  {/* close inner flex flex-col */}
    </div>
  );
}
